import { Component, computed, effect, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { startWith, switchMap } from 'rxjs';
import { DashboardRefreshIntervalService } from '../../../core/services/dashboard-refresh-interval.service';
import { ErrorMessagesComponent } from '../error-messages/error-messages.component';
import { IDropdownOption, MatDropdown } from '../mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../mat-dropdown/scw-mat-dropdown.interface';
import {
  IScreenLockPayload,
  SCREEN_LOCK_NEVER_ID,
  SCREEN_LOCK_PIN_PATTERN,
} from './screen-lock-configuration.interface';

/**
 * Collapsible "Screen Lock" section for the dashboard and slideshow add/edit dialogs. The PIN is never
 * prefilled (the API stores only its hash), so in edit mode an empty field means "keep the current PIN".
 */
@Component({
  imports: [
    MatExpansionModule,
    MatIcon,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatDropdown,
    ErrorMessagesComponent,
    ReactiveFormsModule,
    TranslateModule,
  ],
  selector: 'app-screen-lock-configuration',
  standalone: true,
  styleUrl: './screen-lock-configuration.component.scss',
  templateUrl: './screen-lock-configuration.component.html',
})
export class ScreenLockConfigurationComponent {
  public durationControl = input.required<FormControl<IDropdownOption[] | null>>();
  public pinControl = input.required<FormControl<string | null>>();
  public isExpanded = input<boolean>(false);
  public isFormSubmitted = input<boolean>(false);
  /** True once the entity has a stored PIN, so edit mode can offer "leave blank to keep". */
  public isAutoLockConfigured = input<boolean>(false);
  /** Slideshows fall back to their dashboards' configuration; boards do not. */
  public showInheritanceHint = input<boolean>(false);

  private readonly translate = inject(TranslateService);
  private readonly refreshIntervalService = inject(DashboardRefreshIntervalService);

  public readonly durationOptions: IDropdownOption[] = this.refreshIntervalService.autoLockDurationOptions;

  public readonly durationDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasMatError: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.autoLockDuration'),
  };

  /**
   * A `FormControl`'s `value` is NOT reactive — a `computed` reading it caches forever. Bridge `valueChanges`
   * into a signal instead (MatDropdown writes via setValue, which notifies no signal). See SCREEN_LOCK_CONTEXT.md.
   */
  private readonly durationValue = toSignal(
    toObservable(this.durationControl).pipe(
      switchMap((durationControl) => durationControl.valueChanges.pipe(startWith(durationControl.value))),
    ),
    { initialValue: null as IDropdownOption[] | null },
  );

  private readonly selectedDurationId = computed<number>(
    () => ScreenLockConfigurationComponent.extractDurationId(this.durationValue()) ?? SCREEN_LOCK_NEVER_ID,
  );

  public readonly isLockEnabled = computed<boolean>(() => this.selectedDurationId() !== SCREEN_LOCK_NEVER_ID);

  // `field.leaveBlankIfNoChangeDisclaimer` already exists for exactly this situation elsewhere in the app.
  public readonly pinHintKey = computed<string>(() =>
    this.isAutoLockConfigured() ? 'field.leaveBlankIfNoChangeDisclaimer' : 'page.screenLock.pinRequiredHint',
  );

  public readonly pinErrorStateMatcher: ErrorStateMatcher = {
    isErrorState: (control) => Boolean(control?.invalid) && this.isFormSubmitted(),
  };

  constructor() {
    // Enforce the both-or-neither pairing client-side so the admin never hits the API's 400.
    effect(() => {
      const pinControl: FormControl<string | null> = this.pinControl();
      const validators = [Validators.pattern(SCREEN_LOCK_PIN_PATTERN)];

      if (!this.isLockEnabled()) {
        pinControl.reset(null, { emitEvent: false });
        pinControl.setValidators([]);
      } else {
        pinControl.setValidators(this.isAutoLockConfigured() ? validators : [Validators.required, ...validators]);
      }

      pinControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  /** Builds the API payload. An untouched PIN is omitted (not null) so "leave blank keeps the PIN"; Never clears both columns. */
  public static toPayload(
    durationValue: IDropdownOption[] | null | undefined,
    pinValue: string | null | undefined,
  ): IScreenLockPayload {
    const durationId: number | null = ScreenLockConfigurationComponent.extractDurationId(durationValue);
    const trimmedPin: string = (pinValue ?? '').trim();

    if (durationId === null || durationId === SCREEN_LOCK_NEVER_ID) {
      return { autoLockDurationSeconds: null, autoLockPin: null };
    }

    return {
      autoLockDurationSeconds: durationId,
      ...(trimmedPin ? { autoLockPin: trimmedPin } : {}),
    };
  }

  /** Turns an API duration back into the dropdown selection, for opening the edit dialog. */
  public static toDurationSelection(
    durationSeconds: number | null | undefined,
    options: IDropdownOption[],
  ): IDropdownOption[] {
    const matched: IDropdownOption | undefined = options.find(({ id }) => id === durationSeconds);

    return matched ? [matched] : options.filter(({ id }) => id === SCREEN_LOCK_NEVER_ID);
  }

  private static extractDurationId(value: IDropdownOption[] | null | undefined): number | null {
    return value?.length ? value[0].id : null;
  }
}
